import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Search, Users, Building2, UsersRound, FileText, Loader2, 
  UserPlus, MessageCircle, Church, MapPin, Star, ExternalLink
} from "lucide-react";
import { searchUsers, getGroups, getBusinessListings, getPosts, sendFriendRequest, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

export default function SearchResultsPage({ currentUser }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(false);
  
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [posts, setPosts] = useState([]);
  const [friendRequestSent, setFriendRequestSent] = useState({});

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (q) => {
    if (!q.trim()) return;
    
    setLoading(true);
    try {
      const [membersRes, groupsRes, businessesRes, postsRes] = await Promise.all([
        searchUsers(q, { limit: 10 }).catch(() => ({ data: { users: [] } })),
        getGroups({ search: q, limit: 10 }).catch(() => ({ data: [] })),
        getBusinessListings({ search: q, limit: 10 }).catch(() => ({ data: { listings: [] } })),
        getPosts({ search: q, limit: 10 }).catch(() => ({ data: [] }))
      ]);
      
      setMembers(membersRes.data.users || membersRes.data || []);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data.groups || []));
      setBusinesses(businessesRes.data.listings || businessesRes.data || []);
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : (postsRes.data.posts || []));
    } catch (err) {
      console.error("Search failed:", err);
      toast.error("Search failed");
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      await sendFriendRequest(userId);
      setFriendRequestSent(prev => ({ ...prev, [userId]: true }));
      toast.success("Friend request sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
  };

  const totalResults = members.length + groups.length + businesses.length + posts.length;

  const getCategoryIcon = (category) => {
    const icons = {
      contractor: "🔧", restaurant: "🍽️", retail: "🛍️", professional: "💼",
      health: "🏥", education: "📚", automotive: "🚗", home: "🏠",
      beauty: "💇", tech: "💻", finance: "💰", legal: "⚖️",
      real_estate: "🏘️", entertainment: "🎭", fitness: "💪", pet: "🐾",
      photography: "📷", event: "🎉", cleaning: "🧹", landscaping: "🌳",
      plumbing: "🔧", electrical: "⚡", hvac: "❄️", roofing: "🏠", other: "📌"
    };
    return icons[category] || "📌";
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Search Header */}
        <div className="mb-6">
          <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] mb-4">Search Results</h1>
          
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search members, groups, businesses, posts..."
                className="pl-10 border-[#E5E0D5] font-['Nunito']"
                data-testid="search-input"
              />
            </div>
            <Button 
              type="submit" 
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </Button>
          </form>
          
          {query && !loading && (
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-2">
              Found {totalResults} result{totalResults !== 1 ? 's' : ''} for "{query}"
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
          </div>
        ) : !query ? (
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="py-12 text-center">
              <Search className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">
                Search Parish
              </h3>
              <p className="text-sm text-[#6B665E] font-['Nunito']">
                Search for members, groups, businesses, and posts
              </p>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white border border-[#E5E0D5] p-1 mb-4">
              <TabsTrigger value="all" className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                All ({totalResults})
              </TabsTrigger>
              <TabsTrigger value="members" className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-1" /> Members ({members.length})
              </TabsTrigger>
              <TabsTrigger value="groups" className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                <UsersRound className="w-4 h-4 mr-1" /> Groups ({groups.length})
              </TabsTrigger>
              <TabsTrigger value="businesses" className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                <Building2 className="w-4 h-4 mr-1" /> Businesses ({businesses.length})
              </TabsTrigger>
              <TabsTrigger value="posts" className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white">
                <FileText className="w-4 h-4 mr-1" /> Posts ({posts.length})
              </TabsTrigger>
            </TabsList>

            {/* All Results Tab */}
            <TabsContent value="all" className="space-y-6">
              {/* Members Section */}
              {members.length > 0 && (
                <div>
                  <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[var(--liturgical-primary)]" /> Members
                  </h2>
                  <div className="space-y-2">
                    {members.slice(0, 3).map((member) => (
                      <MemberCard 
                        key={member.user_id} 
                        member={member} 
                        navigate={navigate}
                        onFriendRequest={handleSendFriendRequest}
                        friendRequestSent={friendRequestSent}
                      />
                    ))}
                    {members.length > 3 && (
                      <Button variant="ghost" onClick={() => setActiveTab("members")} className="w-full text-[var(--liturgical-primary)]">
                        View all {members.length} members
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Groups Section */}
              {groups.length > 0 && (
                <div>
                  <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    <UsersRound className="w-5 h-5 text-[var(--liturgical-primary)]" /> Groups
                  </h2>
                  <div className="space-y-2">
                    {groups.slice(0, 3).map((group) => (
                      <GroupCard key={group.group_id} group={group} navigate={navigate} />
                    ))}
                    {groups.length > 3 && (
                      <Button variant="ghost" onClick={() => setActiveTab("groups")} className="w-full text-[var(--liturgical-primary)]">
                        View all {groups.length} groups
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Businesses Section */}
              {businesses.length > 0 && (
                <div>
                  <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[var(--liturgical-primary)]" /> Businesses
                  </h2>
                  <div className="space-y-2">
                    {businesses.slice(0, 3).map((business) => (
                      <BusinessCard key={business.listing_id} business={business} navigate={navigate} getCategoryIcon={getCategoryIcon} />
                    ))}
                    {businesses.length > 3 && (
                      <Button variant="ghost" onClick={() => setActiveTab("businesses")} className="w-full text-[var(--liturgical-primary)]">
                        View all {businesses.length} businesses
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Posts Section */}
              {posts.length > 0 && (
                <div>
                  <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[var(--liturgical-primary)]" /> Posts
                  </h2>
                  <div className="space-y-2">
                    {posts.slice(0, 3).map((post) => (
                      <PostCard key={post.post_id} post={post} navigate={navigate} />
                    ))}
                    {posts.length > 3 && (
                      <Button variant="ghost" onClick={() => setActiveTab("posts")} className="w-full text-[var(--liturgical-primary)]">
                        View all {posts.length} posts
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {totalResults === 0 && (
                <Card className="bg-white border-[#E5E0D5]">
                  <CardContent className="py-12 text-center">
                    <Search className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
                    <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">
                      No results found
                    </h3>
                    <p className="text-sm text-[#6B665E] font-['Nunito']">
                      Try different keywords or check your spelling
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members" className="space-y-2">
              {members.map((member) => (
                <MemberCard 
                  key={member.user_id} 
                  member={member} 
                  navigate={navigate}
                  onFriendRequest={handleSendFriendRequest}
                  friendRequestSent={friendRequestSent}
                />
              ))}
              {members.length === 0 && <NoResults type="members" />}
            </TabsContent>

            {/* Groups Tab */}
            <TabsContent value="groups" className="space-y-2">
              {groups.map((group) => (
                <GroupCard key={group.group_id} group={group} navigate={navigate} />
              ))}
              {groups.length === 0 && <NoResults type="groups" />}
            </TabsContent>

            {/* Businesses Tab */}
            <TabsContent value="businesses" className="space-y-2">
              {businesses.map((business) => (
                <BusinessCard key={business.listing_id} business={business} navigate={navigate} getCategoryIcon={getCategoryIcon} />
              ))}
              {businesses.length === 0 && <NoResults type="businesses" />}
            </TabsContent>

            {/* Posts Tab */}
            <TabsContent value="posts" className="space-y-2">
              {posts.map((post) => (
                <PostCard key={post.post_id} post={post} navigate={navigate} />
              ))}
              {posts.length === 0 && <NoResults type="posts" />}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function MemberCard({ member, navigate, onFriendRequest, friendRequestSent }) {
  return (
    <Card className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Avatar 
            className="w-12 h-12 cursor-pointer"
            onClick={() => navigate(`/profile/${member.user_id}`)}
          >
            <AvatarImage src={resolveImageUrl(member.picture)} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
              {member.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/profile/${member.user_id}`)}>
            <h3 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] truncate">
              {member.name}
            </h3>
            {member.parish_name && (
              <p className="text-sm text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                <Church className="w-3 h-3" /> {member.parish_name}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/messages?to=${member.user_id}`)}
              className="border-[#E5E0D5]"
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
            {!member.is_friend && !friendRequestSent[member.user_id] && (
              <Button
                size="sm"
                onClick={() => onFriendRequest(member.user_id)}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white"
              >
                <UserPlus className="w-4 h-4" />
              </Button>
            )}
            {friendRequestSent[member.user_id] && (
              <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)]">Pending</Badge>
            )}
            {member.is_friend && (
              <Badge className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]">Friend</Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GroupCard({ group, navigate }) {
  return (
    <Card 
      className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/groups/${group.group_id}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
            <UsersRound className="w-6 h-6 text-[var(--liturgical-primary)]" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] truncate">
              {group.name}
            </h3>
            <p className="text-sm text-[#6B665E] font-['Nunito'] flex items-center gap-2">
              <span>{group.member_count || 0} members</span>
              <span>•</span>
              <Badge variant="outline" className="text-xs">{group.privacy || 'public'}</Badge>
            </p>
          </div>
          
          <ExternalLink className="w-4 h-4 text-[#6B665E]" />
        </div>
      </CardContent>
    </Card>
  );
}

function BusinessCard({ business, navigate, getCategoryIcon }) {
  return (
    <Card 
      className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/directory/${business.listing_id}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center text-2xl">
            {getCategoryIcon(business.category)}
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] truncate">
              {business.business_name}
            </h3>
            <p className="text-sm text-[#6B665E] font-['Nunito'] flex items-center gap-2">
              {business.service_area && (
                <>
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{business.service_area}</span>
                </>
              )}
              {business.average_rating > 0 && (
                <>
                  <span>•</span>
                  <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />
                  <span>{business.average_rating.toFixed(1)}</span>
                </>
              )}
            </p>
          </div>
          
          <ExternalLink className="w-4 h-4 text-[#6B665E]" />
        </div>
      </CardContent>
    </Card>
  );
}

function PostCard({ post, navigate }) {
  return (
    <Card 
      className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/dashboard`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="w-10 h-10">
            <AvatarImage src={resolveImageUrl(post.author_picture)} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">
              {post.author_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#6B665E] font-['Nunito'] mb-1">
              <span className="font-semibold text-[#2D2A26]">{post.author_name}</span>
              {post.parish_name && <span> • {post.parish_name}</span>}
            </p>
            <p className="text-[#2D2A26] font-['Nunito'] line-clamp-2">
              {post.content}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NoResults({ type }) {
  return (
    <Card className="bg-white border-[#E5E0D5]">
      <CardContent className="py-8 text-center">
        <p className="text-sm text-[#6B665E] font-['Nunito']">
          No {type} found matching your search
        </p>
      </CardContent>
    </Card>
  );
}
